import { Message } from "features";
import styles from "./styles.module.scss";
import React from "react";
import { DocsIcon, GoogleIcon } from "assets";
import { toTitleCase } from "utils/titleCase";
import { useAppSelector } from "state/hooks";
import { DocumentPreviewModal } from "components/chat/document-preview-modal";
import { getDocumentChip } from "helpers/documentMessage";

interface ChatUserProps {
  message: Message;
  isCandidate?: boolean;
}

const ChatUser: React.FC<ChatUserProps> = ({ message, isCandidate }) => {
  const { content } = message;
  const [showPreview, setShowPreview] = React.useState(false);
  const user = useAppSelector((state) => state.user);
  const { companyLogo, company } = user;
  const initials = company
    ? company.split(/\s+/).map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()
    : '';

  // Whether this reads as a document chip or a plain bubble is decided in one
  // shared place (helpers/documentMessage) so every flow — recruiter, audit,
  // live and replayed-from-history — makes the same call.
  const documentChip = getDocumentChip(message, { isCandidate });
  const canPreview = !!documentChip && !!documentChip.body.trim();

  const chip = documentChip && (
    <div className={styles.documentInfo}>
      <div className={styles.documentIcon}>
        <DocsIcon style={{ color: "#012169" }} />
      </div>
      <div>
        <p className={styles.documentName}>{toTitleCase(documentChip.name)}</p>
        <p className={styles.documentTitle}>
          {documentChip.type}
          {canPreview && <span className={styles.documentHint}> · Click to preview</span>}
        </p>
      </div>
    </div>
  );

  return (
    <div className={styles.chatUser}>
      <div className={styles.message}>
        {documentChip ? (
          canPreview ? (
            <button
              type="button"
              onClick={() => setShowPreview(true)}
              className={styles.documentButton}
              aria-label={`Preview ${documentChip.name}`}
            >
              {chip}
            </button>
          ) : (
            chip
          )
        ) : content?.includes("_") ? (
          toTitleCase(content)
        ) : (
          content
        )}
      </div>
      {canPreview && documentChip && (
        <DocumentPreviewModal
          show={showPreview}
          onClose={() => setShowPreview(false)}
          name={toTitleCase(documentChip.name)}
          type={documentChip.type}
          content={documentChip.body}
        />
      )}
      {isCandidate ? (
        <div className="w-8 h-8 rounded-full overflow-hidden bg-white border border-gray-300 flex items-center justify-center select-none">
          <GoogleIcon />
        </div>
      ) : companyLogo ? (
        <img src={companyLogo} alt="Company Logo" className={styles.icon} />
      ) : (
        <div className="w-8 h-8 rounded-full overflow-hidden bg-white border border-gray-300 flex items-center justify-center text-[#012169] font-semibold text-xs select-none">
          {initials}
        </div>
      )}
    </div>
  );
};

export { ChatUser };
